import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown } from 'lucide-react';
import { Id } from '@hero/schema';
import { cn } from '@hero/web-lib';
import {
  useSelectAsync,
  UseSelectAsyncOptions,
  type OptionDisplay,
  type SelectionValue,
  type SelectAsyncOption,
  type SelectAsyncMode,
  type SelectAsyncOptionDisplayStyle,
  type GetOptionDisplay,
} from '@hero/web-hooks';
import { Button } from '../../../ui/Button';
import { InputLabel } from '../../../ui/Input';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/Popover';
import { OPTION_DISPLAY_COLOR_MAP } from './constants';
import { SelectAsyncList } from './SelectAsyncList';
import { SelectAsyncOptionRow } from './SelectAsyncOptionRow';
import { SelectAsyncSearchHeader } from './SelectAsyncSearchHeader';
import { SelectAsyncTriggerContent } from './SelectAsyncTriggerContent';

export type {
  OptionDisplay,
  SelectionValue,
  SelectAsyncOption,
  SelectAsyncMode,
  SelectAsyncOptionDisplayStyle,
  GetOptionDisplay,
};

export interface SelectAsyncProps<T extends { _id: Id }> extends UseSelectAsyncOptions<T> {
  placeholder?: string;
  label?: string;
  required?: boolean;
  createNewLabel?: string;
  onCreateNew?: () => void;
  allowCreateNew?: boolean;
  allowEdit?: boolean;
  onEdit?: (item: T) => void;
  searchPlaceholder?: string;
  emptyMessage?: string;
  loadingMessage?: string;
  renderOption?: (item: T, context: { isSelected: boolean }) => React.ReactNode | OptionDisplay;
  renderTrigger?: (selected: T | T[] | null) => React.ReactNode;
  optionDisplayStyle?: SelectAsyncOptionDisplayStyle;
  getOptionDisplay?: GetOptionDisplay<T>;
  disabled?: boolean;
  className?: string;
  popoverZIndex?: string;
  disablePortal?: boolean;
  noMoreItemsMessage?: string;
  /** When true, keep the popover open (e.g. while an edit modal is open). */
  keepOpenOnEdit?: boolean;
}

export function SelectAsync<T extends { _id: Id } = any>(props: SelectAsyncProps<T>) {
  const { t } = useTranslation(['c-main']);
  const {
    mode = 'single',
    value,
    onChangeSelect,
    listQuery,
    singleQuery,
    getItemId,
    filterSelectionToCurrentList,
    searchDebounceMs,
    placeholder = t('c-main:selection.select'),
    createNewLabel = t('c-main:crud_buttons.create_new'),
    onCreateNew,
    allowCreateNew = true,
    allowEdit = false,
    onEdit,
    searchPlaceholder = t('search.placeholder'),
    emptyMessage = t('c-main:status.no_results_found'),
    loadingMessage = t('c-main:async_operations.loading_more'),
    noMoreItemsMessage = t('c-main:status.no_more_items'),
    label,
    required,
    renderOption,
    renderTrigger,
    optionDisplayStyle,
    getOptionDisplay,
    disabled = false,
    className,
    popoverZIndex,
    disablePortal = false,
    keepOpenOnEdit: keepOpen = false,
  } = props;

  const showAvatar = optionDisplayStyle?.showAvatar !== false;
  const colorKey = optionDisplayStyle?.color ?? 'default';
  const colorClasses = OPTION_DISPLAY_COLOR_MAP[colorKey];
  const useBuiltInDisplay = optionDisplayStyle != null && !renderOption && colorClasses != null;
  const useBuiltInTrigger = optionDisplayStyle != null && !renderTrigger && colorClasses != null;

  const defaultGetItemId = (item: T): Id => {
    const any = item as Record<string, unknown>;
    return (any._id ?? any.id) as Id;
  };
  const effectiveGetItemId = getItemId ?? defaultGetItemId;

  const selector = useSelectAsync({
    mode,
    value,
    onChangeSelect,
    listQuery,
    singleQuery,
    getItemId: effectiveGetItemId,
    filterSelectionToCurrentList,
    searchDebounceMs,
    disabled,
  });

  const displayOptionsSingle = React.useMemo(() => {
    if (mode !== 'single' || !selector.selectedItems || selector.selectedItems.length !== 1) {
      return selector.options as T[];
    }
    const selected = selector.selectedItems[0];
    const selectedId = effectiveGetItemId(selected);
    const rest = (selector.options as T[]).filter(
      o => effectiveGetItemId(o).toString() !== selectedId.toString(),
    );
    return [selected, ...rest];
  }, [mode, selector.selectedItems, selector.options, effectiveGetItemId]);

  const listOptions = mode === 'single' ? displayOptionsSingle : (selector.options as T[]);

  const mainListOptions =
    mode === 'multi'
      ? (selector.options as T[]).filter(item => !selector.isSelected(effectiveGetItemId(item)))
      : listOptions;

  const getDisplayFromItem = (item: T): { label: string; desc?: string; subDesc?: string } => {
    if (getOptionDisplay) return getOptionDisplay(item);
    const rec = item as Record<string, unknown>;
    const labelStr = String(rec.name ?? rec.label ?? effectiveGetItemId(item));
    return {
      label: labelStr,
      desc: rec.desc != null ? String(rec.desc) : undefined,
      subDesc: rec.subDesc != null ? String(rec.subDesc) : undefined,
    };
  };

  const renderOptionRow = (item: T, isSelected: boolean) => (
    <SelectAsyncOptionRow<T>
      item={item}
      isSelected={isSelected}
      mode={mode}
      getItemId={effectiveGetItemId}
      getDisplayFromItem={getDisplayFromItem}
      optionDisplayStyle={optionDisplayStyle ?? null}
      colorClasses={colorClasses}
      showAvatar={showAvatar}
      renderOption={renderOption}
      allowEdit={allowEdit && !!onEdit}
      onEdit={onEdit}
    />
  );

  const handleCreateNewClick = React.useCallback(() => {
    onCreateNew?.();
    selector.setOpen(false);
    selector.clearSearch();
  }, [onCreateNew, selector]);

  const selectedForTrigger =
    !selector.selectedItems || selector.selectedItems.length === 0
      ? null
      : mode === 'single'
      ? selector.selectedItems[0]
      : selector.selectedItems;

  const popoverOpen = selector.open || keepOpen;

  const popover = (
    <Popover
      open={popoverOpen}
      onOpenChange={(newOpen: boolean) => {
        if (newOpen === false && keepOpen) return;
        selector.setOpen(newOpen);
        if (!newOpen) selector.clearSearch();
      }}
      modal={false}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={selector.open}
          disabled={disabled}
          className={cn(
            'w-full justify-between text-start bg-gray-100 border rounded-lg body1 text-[#919EAB] px-3 py-2 h-auto min-h-[42px] rtl:flex-row-reverse',
            selector.selectedItems && selector.selectedItems.length > 0 && 'text-gray-900',
            className,
          )}>
          <SelectAsyncTriggerContent<T>
            selected={selectedForTrigger}
            loading={selector.selectedItemsLoading}
            placeholder={placeholder}
            loadingMessage={loadingMessage}
            mode={mode}
            getItemId={effectiveGetItemId}
            getDisplayFromItem={getDisplayFromItem}
            useBuiltInTrigger={useBuiltInTrigger}
            showAvatar={showAvatar}
            colorClasses={colorClasses}
            renderTrigger={renderTrigger}
          />
          <ChevronsUpDown className="w-4 h-4 ms-2 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          'w-[--radix-popover-trigger-width] min-w-[350px] max-h-[400px] p-0 flex flex-col',
          popoverZIndex,
        )}
        align="start"
        disablePortal={disablePortal}>
        <SelectAsyncSearchHeader
          searchValue={selector.searchInputValue}
          onSearchChange={value =>
            selector.onSearchInputChange({
              target: { value },
            } as React.ChangeEvent<HTMLInputElement>)
          }
          searchPlaceholder={searchPlaceholder}
          placeholder={placeholder}
          allowCreateNew={allowCreateNew && !!onCreateNew}
          onCreateNewClick={onCreateNew ? handleCreateNewClick : undefined}
          createNewLabel={createNewLabel}
          isSearching={selector.isSearching}
          optionsLength={selector.options.length}
        />
        <SelectAsyncList<T>
          mode={mode}
          selectedItems={selector.selectedItems}
          mainListOptions={mainListOptions}
          handleSelect={selector.handleSelect}
          getItemId={effectiveGetItemId}
          isSelected={selector.isSelected}
          renderOptionRow={renderOptionRow}
          listContainerRef={
            selector.listContainerRef as React.MutableRefObject<HTMLDivElement | null>
          }
          setListRefReady={selector.setListRefReady}
          loadMoreRef={selector.loadMoreRef}
          isSearching={selector.isSearching}
          isFetchingNextPage={selector.isFetchingNextPage}
          hasNextPage={selector.hasNextPage}
          isLoading={selector.isLoading}
          optionsLength={selector.options.length}
          emptyMessage={emptyMessage}
          loadingMessage={loadingMessage}
          noMoreItemsMessage={noMoreItemsMessage}
          allowCreateNew={allowCreateNew}
          onCreateNewClick={onCreateNew ? handleCreateNewClick : undefined}
          createNewLabel={createNewLabel}
          selectedCountLabel={t('c-main:selection.selected_count', {
            count: selector.selectedItems?.length ?? 0,
          })}
        />
      </PopoverContent>
    </Popover>
  );

  if (label) {
    return (
      <div className="space-y-2 w-full">
        <InputLabel label={label} required={required} className="mb-0" />
        {popover}
      </div>
    );
  }
  return popover;
}
